import {
  BILLING_NUDGE_TZ,
  dateStringForInstantInTimeZone,
  dateStringInTimeZone,
  isOrgPaysCurrentlyEffective,
} from './billingPaywallNudge'

export const FEATURE_BILLING_CODES = {
  HEALTH_ANALYSIS: 'HEALTH_ANALYSIS',
  HIDDEN_INSURANCE: 'HIDDEN_INSURANCE',
}

/**
 * @typedef {'SUBSCRIPTION_REQUIRED' | 'SUBSCRIPTION_EXPIRED' | 'SUBSCRIPTION_SUSPENDED' | 'FEATURE_NOT_ENABLED' | 'QUOTA_EXCEEDED'} FeatureBillingBlockReason
 * @typedef {{
 *   canUse: boolean,
 *   reason: FeatureBillingBlockReason|null,
 *   remaining: number|null,
 *   limit: number|null,
 *   unlimited: boolean,
 *   orgPays: boolean,
 * }} FeatureBillingGate
 */

/**
 * @param {Record<string, unknown>|null|undefined} billingMe
 * @param {string} featureCode
 * @returns {FeatureBillingGate}
 */
export function resolveFeatureBillingGate(billingMe, featureCode) {
  const emptyBlocked = (/** @type {FeatureBillingBlockReason} */ reason) => ({
    canUse: false,
    reason,
    remaining: null,
    limit: null,
    unlimited: false,
    orgPays: false,
  })

  if (!billingMe || typeof billingMe !== 'object') {
    return emptyBlocked('SUBSCRIPTION_REQUIRED')
  }

  if (isOrgPaysCurrentlyEffective(billingMe.orgContract)) {
    return {
      canUse: true,
      reason: null,
      remaining: null,
      limit: null,
      unlimited: true,
      orgPays: true,
    }
  }

  const subscription = billingMe.subscription && typeof billingMe.subscription === 'object'
    ? billingMe.subscription
    : null
  if (!subscription) {
    return emptyBlocked('SUBSCRIPTION_REQUIRED')
  }

  const status = String(subscription.status || '').toUpperCase()
  if (status === 'SUSPENDED') {
    return emptyBlocked('SUBSCRIPTION_SUSPENDED')
  }
  if (status === 'EXPIRED') {
    return emptyBlocked('SUBSCRIPTION_EXPIRED')
  }

  const endsAtYmd = dateStringForInstantInTimeZone(/** @type {string|undefined} */ (subscription.endsAt), BILLING_NUDGE_TZ)
  const todayYmd = dateStringInTimeZone(new Date(), BILLING_NUDGE_TZ)
  if (endsAtYmd && todayYmd && endsAtYmd < todayYmd) {
    return emptyBlocked('SUBSCRIPTION_EXPIRED')
  }

  if (status !== 'ACTIVE' && status !== 'TRIAL') {
    return emptyBlocked('SUBSCRIPTION_REQUIRED')
  }

  const features = Array.isArray(billingMe.features) ? billingMe.features : []
  const policy = features.find((row) => String(row?.featureCode || '') === featureCode)
  if (policy && policy.enabled === false) {
    return emptyBlocked('FEATURE_NOT_ENABLED')
  }

  const period = subscription.currentQuotaPeriod && typeof subscription.currentQuotaPeriod === 'object'
    ? subscription.currentQuotaPeriod
    : null
  const counters = period && Array.isArray(period.counters) ? period.counters : []
  const counter = counters.find((row) => String(row?.featureCode || '') === featureCode)

  const quotaMode = String(counter?.quotaMode || policy?.quotaMode || '').toUpperCase()
  if (quotaMode === 'UNLIMITED') {
    return {
      canUse: true,
      reason: null,
      remaining: null,
      limit: null,
      unlimited: true,
      orgPays: false,
    }
  }

  if (!counter) {
    if (policy?.enabled === true && (quotaMode === 'NONE' || quotaMode === '')) {
      return {
        canUse: true,
        reason: null,
        remaining: null,
        limit: null,
        unlimited: false,
        orgPays: false,
      }
    }
    return emptyBlocked('FEATURE_NOT_ENABLED')
  }

  const limitRaw = counter.limitValue ?? counter.monthlyLimit ?? policy?.monthlyLimit ?? policy?.monthlyQuota
  const limit = limitRaw == null ? null : Number(limitRaw)
  const remainingRaw = counter.remainingValue ?? counter.remaining
  const remaining = remainingRaw == null
    ? (limit == null ? null : Math.max(0, limit - Number(counter.usedValue ?? counter.used ?? 0) - Number(counter.reservedValue ?? counter.reserved ?? 0)))
    : Number(remainingRaw)

  if (limit != null && remaining != null && remaining <= 0) {
    return {
      canUse: false,
      reason: 'QUOTA_EXCEEDED',
      remaining: 0,
      limit,
      unlimited: false,
      orgPays: false,
    }
  }

  return {
    canUse: true,
    reason: null,
    remaining,
    limit,
    unlimited: false,
    orgPays: false,
  }
}

/** @param {FeatureBillingGate|null|undefined} gate */
export function featureBillingBlockMessage(gate) {
  if (!gate || gate.canUse) return ''
  switch (gate.reason) {
    case 'SUBSCRIPTION_EXPIRED':
      return '구독 기간이 만료되어 이용할 수 없습니다. 구독을 갱신해 주세요.'
    case 'SUBSCRIPTION_SUSPENDED':
      return '구독이 정지되어 이용할 수 없습니다. 고객센터에 문의해 주세요.'
    case 'QUOTA_EXCEEDED':
      return '이번 달 사용 가능 횟수를 모두 사용했습니다.'
    case 'FEATURE_NOT_ENABLED':
      return '현재 구독에서는 이 기능을 사용할 수 없습니다.'
    case 'SUBSCRIPTION_REQUIRED':
    default:
      return '활성 구독이 필요합니다. 구독을 확인해 주세요.'
  }
}

/** @param {FeatureBillingGate|null|undefined} gate */
export function featureBillingQuotaLabel(gate) {
  if (!gate) return '-'
  if (gate.orgPays) return '조직 구독'
  if (gate.unlimited) return '무제한'
  if (gate.remaining == null || gate.limit == null) return '-'
  return `${gate.remaining} / ${gate.limit}`
}

/** @param {unknown} error */
export function isBillingGateApiError(error) {
  const status = error?.response?.status
  const code = String(error?.response?.data?.code || '')
  return status === 402
    || code === 'SUBSCRIPTION_REQUIRED'
    || code === 'FEATURE_QUOTA_EXCEEDED'
    || code === 'FEATURE_NOT_ENABLED'
}
